// 12345
// 1,234.5
// $1,000
// -$1,000
// 123.9%
// (2,800)
// ($2,800)
// (2.3m)
// 2.3m
// 7.9b
// $7.9b
//Todo:
// 5,7m
import { currencySign } from "../constants";

const signs = Object.values(currencySign);

// const isCurrency = (char: string) => {
//   return signs.includes(char);
// };

const getMultiply = (char: string) => {
  switch (char) {
    case "m":
      return 1000000;
    case "b":
      return 1000000000;
    case "t":
      return 1000;
    default:
      return 1;
  }
};

export const numerifyValue = (value: string | number): number => {
  if (typeof value === "number") return value;
  if (!value.length) return NaN;
  let negative = false;
  let multiply = 1;
  let str = value.trim();
  str = str.split(",").join("");

  if (str[0] === "(" && str[str.length - 1] === ")") {
    str = str.slice(1, str.length - 1);
    negative = true;
  } else if (str[0] === "-") {
    str = str.slice(1, str.length);
    negative = true;
  }

  for (const sign of signs) {
    str = str.replace(sign, "");
  }

  if (str[str.length - 1] === "%") {
    str = str.slice(0, str.length - 1);
  }

  multiply = getMultiply(str[str.length - 1]);
  if (multiply > 1) {
    str = str.slice(0, str.length - 1);
  }

  const num = Number(str);
  return num * multiply * (negative ? -1 : 1);
};

export const getDecimalSeparator = (): string => {
  const numberWithDecimal = 1.1;
  const formatter = new Intl.NumberFormat(navigator.language);
  return formatter.format(numberWithDecimal).charAt(1);
};

export const parseNumberFromText = (text: string, decimalSeparator?: string): number => {
  try {
    if (decimalSeparator === ",") {
      // For EU format (e.g., 250.044 -> 250044), remove dots and treat commas as decimal points
      const cleanNumber = text.replace(/\./g, "").replace(/,/g, ".");
      const number = Number(cleanNumber);
      return isNaN(number) ? 0 : number;
    }
    // Default US format
    const cleanNumber = text.replace(/[^\d.]/g, "");
    const number = Number(cleanNumber);
    return isNaN(number) ? 0 : number;
  } catch {
    return 0;
  }
};

export const defineNumberFormat = (text: string): "eu" | "us" => {
  // Clear text from everything except numbers and signs . or , or -
  const cleanedText = text.replace(/[^0-9.,]/g, "");

  let commaCount = 0;
  let dotCount = 0;
  let separatorSigns = "";

  for (const char of cleanedText) {
    if (char === ",") {
      separatorSigns += ",";
      commaCount++;
    }
    if (char === ".") {
      separatorSigns += ".";
      dotCount++;
    }
  }

  if (!separatorSigns.length) return "us";

  if (commaCount > 1) return "us";
  if (dotCount > 1) return "eu";

  // This is EU if first sign is dot and second is comma - 250.044,22
  if (commaCount === 1 && dotCount === 1 && separatorSigns[0] === ".") return "eu";
  // This is US if first sign is comma and second is dot - 250,044.22
  if (commaCount === 1 && dotCount === 1 && separatorSigns[0] === ",") return "us";

  const splitByComma = cleanedText.split(",");
  const splitByDot = cleanedText.split(".");

  if (commaCount === 1 && !dotCount) {
    // If we have only one comma, and more then 2 signs after it, it's US - 100,000 or 20,229
    if (splitByComma[1].length > 2) return "us";

    // 253,30
    if (splitByComma[1].length < 3) return "eu";
  }

  if (dotCount === 1 && !commaCount) {
    // If we have only one dot, and more then 2 signs before and after it, it's EU - 100.000
    if (splitByDot[0].length > 2 && splitByDot[1].length > 2) return "eu";

    // 253.30
    if (splitByDot[1].length < 3) return "us";
  }

  // If we have only one comma, and less then 3 signs before it, it's EU - 2,50, 21,50
  if (commaCount === 1 && !dotCount && splitByComma[0].length < 3) return "eu";
  // If we have only one dot, and less then 3 signs before it, it's US - 2.50, 21.50
  if (dotCount === 1 && !commaCount && splitByDot[0].length < 3) return "us";

  return "us";
};

export const separatedStringToNumber = (text: string, format: "eu" | "us"): number => {
  const cleanedText = text.replace(/[^0-9.,]/g, "");

  // Check for negative values in parentheses or with minus sign
  let isNegative = false;
  const trimmedText = text.trim();
  if (
    (trimmedText[0] === "(" && trimmedText[trimmedText.length - 1] === ")") ||
    text.replace(/[^0-9.,-]/g, "").startsWith("-")
  ) {
    isNegative = true;
  }

  const decimalSeparator = format === "eu" ? "," : "."; // Pick decimal separator
  const [integer, decimal] = cleanedText.split(decimalSeparator); // Split into integer and decimal
  const numbersOnly = integer.replace(/[^0-9]/g, ""); // Remove all non-numeric characters

  const decimalOnly = decimal?.replace(/[^0-9]/g, "");
  const valueWithDecimal = numbersOnly + (decimalOnly ? `.${decimalOnly}` : "");
  const resultNumber = Number(valueWithDecimal); // Convert integer to number
  // Add decimal to integer, decimal.length - signs number after decimal point
  return isNaN(resultNumber) ? 0 : resultNumber * (isNegative ? -1 : 1);
};

interface FormatCurrencyOptions {
  value: number;
  currencySymbol: string;
  abbreviated?: boolean;
}

/**
 * Formats a currency value with proper negative sign placement
 * For negative values: -$100 instead of $-100
 * For positive values: $100
 * @param options - Object containing value, currencySymbol, and optional abbreviated flag
 * @returns Formatted currency string
 */
export const formatCurrency = (options: FormatCurrencyOptions): string => {
  const { value, currencySymbol, abbreviated = false } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return "N/A";
  }

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  if (abbreviated) {
    if (absValue >= 1e9) {
      const formattedValue = (absValue / 1e9).toFixed(1);
      return isNegative ? `-${currencySymbol}${formattedValue}B` : `${currencySymbol}${formattedValue}B`;
    }
    if (absValue >= 1e6) {
      const formattedValue = (absValue / 1e6).toFixed(1);
      return isNegative ? `-${currencySymbol}${formattedValue}M` : `${currencySymbol}${formattedValue}M`;
    }
    if (absValue >= 1e3) {
      const formattedValue = (absValue / 1e3).toFixed(1);
      return isNegative ? `-${currencySymbol}${formattedValue}K` : `${currencySymbol}${formattedValue}K`;
    }
  }

  const formattedNumber = absValue.toLocaleString();

  if (isNegative) {
    return `-${currencySymbol}${formattedNumber}`;
  }

  return `${currencySymbol}${formattedNumber}`;
};

// Example usage:
// formatCurrency({ value: 1234567, currencySymbol: "$" }) // "$1,234,567"
// formatCurrency({ value: -1234567, currencySymbol: "$" }) // "-$1,234,567"
// formatCurrency({ value: 1234567, currencySymbol: "$", abbreviated: true }) // "$1.2M"
// formatCurrency({ value: -1234567, currencySymbol: "$", abbreviated: true }) // "-$1.2M"
